import React, { useEffect, useState } from "react";
import {
  Card, Layout, Tree, Input, Tag, Button, Spin, Empty, Popconfirm, Space, Typography, SideSheet,
  Tabs, TabPane, Modal, Toast,
} from "@douyinfe/semi-ui";
import { IconDelete, IconSearch, IconPlus, IconEdit, IconCopy } from "@douyinfe/semi-icons";
import { ManualApi, Manual as ManualType, ManualCategory, ManualSearchResult } from "./api";
import { ManualEditor } from "./ManualEditor";
import { TemplatePickerModal } from "./TemplatePickerModal";

const { Sider, Content } = Layout;
const { Text } = Typography;

export default function ManualCenter() {
  const [categories, setCategories] = useState<ManualCategory[]>([]);
  const [manuals, setManuals] = useState<ManualType[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
  const [keyword, setKeyword] = useState("");
  const [searchResults, setSearchResults] = useState<ManualSearchResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [readingManual, setReadingManual] = useState<ManualType | null>(null);
  const [editorVisible, setEditorVisible] = useState(false);
  const [editingManual, setEditingManual] = useState<ManualType | null>(null);
  const [tab, setTab] = useState<"manuals" | "templates">("manuals");
  const [tplPickerVisible, setTplPickerVisible] = useState(false);
  const [cloneModalVisible, setCloneModalVisible] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState<number | null>(null);
  const [cloneTitle, setCloneTitle] = useState("");

  const openEditor = (manual: ManualType | null) => {
    setEditingManual(manual);
    setEditorVisible(true);
  };
  const onEditorClose = () => {
    setEditorVisible(false);
    setEditingManual(null);
    loadManuals();
  };

  const loadCategories = async () => {
    const resp = await ManualApi.categoryList();
    setCategories(resp || []);
  };

  const loadManuals = async () => {
    setLoading(true);
    try {
      const resp = tab === "templates"
        ? await ManualApi.templates()
        : await ManualApi.list(selectedCategoryId);
      setManuals(resp || []);
    } catch (e: any) {
      Toast.error("加载失败：" + (e?.message || ""));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCategories(); window.document.title = "操作手册";}, []);

  useEffect(() => {
    if (!keyword) {
      setSearchResults(null);
      loadManuals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryId, keyword, tab]);

  const onSearch = async (val: string) => {
    setKeyword(val);
    if (val.trim().length >= 2) {
      const resp = await ManualApi.search(val.trim(), selectedCategoryId);
      setSearchResults(resp || []);
    } else {
      setSearchResults(null);
    }
  };

  const escapeHtml = (s: string): string =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

  const highlightSnippet = (snippet: string, kw: string) => {
    const escaped = escapeHtml(snippet || "");
    if (!kw) return escaped;
    const escapedKw = escapeHtml(kw);
    const idx = escaped.toLowerCase().indexOf(escapedKw.toLowerCase());
    if (idx < 0) return escaped;
    return escaped.substring(0, idx) +
      "<mark>" + escaped.substring(idx, idx + escapedKw.length) + "</mark>" +
      escaped.substring(idx + escapedKw.length);
  };

  return (
    <Layout style={{ minHeight: "calc(100vh - 60px)" }}>
      <Sider style={{ background: "#fff", padding: 16, width: 220 }}>
        <Tree
          treeData={[
            { key: "all", label: "全部", value: undefined as any },
            ...categories.map(c => ({ key: String(c.id), label: c.name, value: c.id })),
          ]}
          value={selectedCategoryId === undefined ? "all" : String(selectedCategoryId)}
          onSelect={(_val, node) => {
            const v = (node as any)?.value;
            setSelectedCategoryId(v);
          }}
        />
      </Sider>
      <Content style={{ padding: 16 }}>
        <Card>
          <Space style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }}>
            <Input
              prefix={<IconSearch />}
              placeholder="搜索关键字（标题/正文）"
              value={keyword}
              onChange={onSearch}
              style={{ width: 400 }}
            />
            <Space>
              <Button icon={<IconCopy />} onClick={() => setTplPickerVisible(true)}>从模板创建</Button>
              <Button icon={<IconPlus />} theme="solid" type="primary" onClick={() => openEditor(null)}>新建手册</Button>
            </Space>
          </Space>

          <Spin spinning={loading}>
            {searchResults ? (
              searchResults.length === 0 ? (
                <Empty description="无匹配结果" />
              ) : (
                <div>
                  {searchResults.map(r => (
                    <div
                      key={r.manualId}
                      onClick={() => ManualApi.get(r.slug || r.manualId).then(m => setReadingManual(m))}
                    >
                      <Card
                        shadows="hover"
                        style={{ marginBottom: 12, cursor: "pointer" }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <div>
                            <Text strong style={{ fontSize: 16 }}>{r.title}</Text>
                            <Tag color="green" size="small" style={{ marginLeft: 8 }}>{r.categoryName}</Tag>
                          </div>
                        </div>
                        <div
                          style={{ color: "#666", marginTop: 8, fontSize: 13 }}
                          dangerouslySetInnerHTML={{ __html: "…" + highlightSnippet(r.snippet, keyword) + "…" }}
                        />
                      </Card>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <>
              <Tabs activeKey={tab} onChange={k => setTab(k as any)} style={{ marginBottom: 16 }}>
                <TabPane tab="业务手册" itemKey="manuals" />
                <TabPane tab="通用模板" itemKey="templates" />
              </Tabs>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                {manuals.map(m => (
                  <div
                    key={m.id}
                    onClick={() => setReadingManual(m)}
                  >
                    <Card shadows="hover" style={{ cursor: "pointer" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                        <Text strong>{m.title}</Text>
                        <Space>
                          <Button icon={<IconEdit />} size="small" type="tertiary" theme="borderless"
                                  onClick={(e) => { e.stopPropagation(); openEditor(m); }} />
                          <Button icon={<IconCopy />} size="small" type="tertiary" theme="borderless"
                                  title="另存为模板"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCloneSourceId(m.id);
                                    setCloneTitle(m.title + " - 模板");
                                    setCloneModalVisible(true);
                                  }} />
                          <Popconfirm title="确认删除该手册？" onConfirm={async () => {
                            await ManualApi.remove(m.id); loadManuals();
                          }}>
                            <Button icon={<IconDelete />} size="small" type="danger" theme="borderless"
                                    onClick={(e) => e.stopPropagation()} />
                          </Popconfirm>
                        </Space>
                      </div>
                      <Text type="tertiary" size="small" style={{ display: "block", marginTop: 8 }}>
                        {m.summary || "（无摘要）"}
                      </Text>
                      <Tag color="light-blue" size="small" style={{ marginTop: 8 }}>v{m.versionNo}</Tag>
                    </Card>
                  </div>
                ))}
              </div>
              </>
            )}
          </Spin>
        </Card>
      </Content>

      <SideSheet
        title={readingManual?.title || ""}
        visible={!!readingManual}
        onCancel={() => setReadingManual(null)}
        width="70%"
        placement="right"
        bodyStyle={{ padding: 0 }}
      >
        {readingManual?.slug && (
          <iframe
            src={ManualApi.renderUrl(readingManual.slug)}
            style={{ width: "100%", height: "calc(100vh - 86px)", border: "none" }}
            title={readingManual.title}
          />
        )}
      </SideSheet>

      <ManualEditor
        visible={editorVisible}
        manual={editingManual}
        categories={categories}
        onClose={onEditorClose}
      />

      <TemplatePickerModal
        visible={tplPickerVisible}
        onClose={() => setTplPickerVisible(false)}
        onPicked={(m) => {
          setTab("manuals");
          openEditor(m);
        }}
      />

      <Modal
        title="另存为模板"
        visible={cloneModalVisible}
        onOk={async () => {
          if (!cloneSourceId) return;
          try {
            await ManualApi.cloneAsTemplate(cloneSourceId, cloneTitle);
            Toast.success("已另存为模板");
            setCloneModalVisible(false);
          } catch (e: any) {
            Toast.error("保存失败：" + (e?.message || ""));
          }
        }}
        onCancel={() => setCloneModalVisible(false)}
      >
        <Input value={cloneTitle} onChange={setCloneTitle} placeholder="模板标题" />
      </Modal>
    </Layout>
  );
}
